import React, { lazy } from 'react';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import ProtectedRoute from 'src/pages/utility/ProtectedRoute';
import { getCrumb } from 'src/components/MainPage/_shared/Breadcrumbs/Breadcrumb';
import { SuspenseContainer } from 'src/components/MainPage/_shared/page';
import { roleSet } from 'src/constants/role';
import { queryClient } from '../queryClient';
import type { AppRoute } from '../types';
import { promotionLoader } from 'src/pages/promotions/PromotionPage/promotionLoader';
import { promocodeLoader } from 'src/pages/promotions/PromoCodePage/promocodeLoader';

const PromotionsMenuPage = lazy(
  () => import('src/pages/promotions/PromotionsMenuPage')
);
const BannersPage = lazy(() => import('src/pages/promotions/BannersPage'));
const BannerPage = lazy(() => import('src/pages/promotions/BannerPage'));
const KioskBannersPage = lazy(
  () => import('src/pages/promotions/KioskBannersPage')
);
const KioskBannerPage = lazy(
  () => import('src/pages/promotions/KioskBannerPage')
);
const PromotionsPage = lazy(
  () => import('src/pages/promotions/PromotionsPage')
);
const PromotionPage = lazy(() => import('src/pages/promotions/PromotionPage'));
const PromotionSchedulePage = lazy(
  () => import('src/pages/promotions/PromotionSchedulePage')
);
const PromoCodesPage = lazy(
  () => import('src/pages/promotions/PromoCodesPage')
);
const PromoCodePage = lazy(() => import('src/pages/promotions/PromoCodePage'));
const MailingListsPage = lazy(
  () => import('src/pages/promotions/MailingListsPage')
);
const MailingListPage = lazy(
  () => import('src/pages/promotions/MailingListPage')
);
const AdditionalSaleOffersPage = lazy(
  () => import('src/pages/promotions/AdditionalSaleOffersPage')
);
const AdditionalSaleOfferPage = lazy(
  () => import('src/pages/promotions/AdditionalSaleOfferPage')
);
const StoriesPage = lazy(() => import('src/pages/promotions/StoriesPage'));
const StoryPage = lazy(() => import('src/pages/promotions/StoryPage'));
const SplashScreensPage = lazy(
  () => import('src/pages/promotions/SplashScreensPage')
);
const SplashScreenPage = lazy(
  () => import('src/pages/promotions/SplashScreenPage')
);

export const promotionsRoutes: AppRoute[] = [
  {
    element: <ProtectedRoute roles={roleSet.cashierAndOperatorToSuperAdmin} />,
    handle: {
      label: 'Промоакции',
      icon: <LoyaltyIcon />,
      roles: roleSet.cashierAndOperatorToSuperAdmin,
    },
    children: [
      {
        element: <ProtectedRoute roles={roleSet.adminToSuperAdmin} />,
        children: [
          {
            path: `promotions-menu`,
            element: <PromotionsMenuPage />,
            handle: { label: 'Меню акций', roles: roleSet.adminToSuperAdmin },
          },
          {
            path: `promotions`,
            handle: {
              crumb: getCrumb('Акции', true),
            },
            children: [
              {
                index: true,
                element: <PromotionsPage />,
                handle: {
                  label: 'Акции',
                  roles: roleSet.adminToSuperAdmin,
                },
              },
              {
                path: `:id`,
                element: <PromotionPage />,
                loader: promotionLoader,
              },
            ],
          },
          {
            path: `promotionSchedule`,
            element: <PromotionSchedulePage />,
            handle: {
              label: 'График работы акций',
              roles: roleSet.adminToSuperAdmin,
            },
          },
        ],
      },
      {
        path: `promocodes`,
        handle: {
          crumb: getCrumb('Промокоды', true),
        },
        children: [
          {
            index: true,
            element: <PromoCodesPage />,
            handle: {
              label: 'Промокоды',
              roles: roleSet.cashierAndOperatorToSuperAdmin,
            },
          },
          {
            path: `:id`,
            element: <PromoCodePage />,
            loader: promocodeLoader(queryClient),
          },
        ],
      },
      {
        path: `mailing-lists`,
        handle: {
          crumb: getCrumb('Рассылки', true),
        },
        children: [
          {
            index: true,
            element: <MailingListsPage />,
            handle: {
              label: 'Рассылки',
              roles: roleSet.adminToSuperAdmin,
              nonFranchiseeAccessOnly: true,
            },
          },
          {
            path: `:id`,
            element: (
              <SuspenseContainer>
                <MailingListPage />
              </SuspenseContainer>
            ),
            async lazy() {
              const { mailingListLoader } = await import(
                'src/pages/promotions/MailingListPage/mailingListLoader'
              );
              return {
                loader: mailingListLoader,
              };
            },
          },
        ],
      },
      {
        path: `sale-offers`,
        handle: {
          crumb: getCrumb('Доп. продажи', true),
        },
        children: [
          {
            index: true,
            element: <AdditionalSaleOffersPage />,
            handle: {
              label: 'Доп. продажи',
              roles: roleSet.adminToSuperAdmin,
            },
          },
          {
            path: `:id`,
            element: (
              <SuspenseContainer>
                <AdditionalSaleOfferPage />
              </SuspenseContainer>
            ),
            async lazy() {
              const { additionalSaleOfferLoader } = await import(
                'src/pages/promotions/AdditionalSaleOfferPage/additionalSaleOfferLoader'
              );
              return {
                loader: additionalSaleOfferLoader,
              };
            },
          },
        ],
      },
      {
        path: `stories`,
        handle: {
          crumb: getCrumb('Истории', true),
        },
        children: [
          {
            index: true,
            element: <StoriesPage />,
            handle: {
              label: 'Истории',
              roles: roleSet.adminToSuperAdmin,
            },
          },
          {
            path: `:id`,
            element: (
              <SuspenseContainer>
                <StoryPage />
              </SuspenseContainer>
            ),
            async lazy() {
              const { storyLoader } = await import(
                'src/pages/promotions/StoryPage/storyLoader'
              );
              return {
                loader: storyLoader,
              };
            },
          },
        ],
      },
      {
        path: `banners`,
        handle: {
          crumb: getCrumb('Баннеры', true),
        },
        children: [
          {
            index: true,
            element: <BannersPage />,
            handle: {
              label: 'Баннеры',
              roles: roleSet.adminToSuperAdmin,
            },
          },
          {
            path: `:id`,
            element: (
              <SuspenseContainer>
                <BannerPage />
              </SuspenseContainer>
            ),
            async lazy() {
              const { bannerLoader } = await import(
                'src/pages/promotions/BannerPage/bannerLoader'
              );
              return {
                loader: bannerLoader,
              };
            },
          },
        ],
      },
      {
        path: `kiosk-banners`,
        handle: {
          crumb: getCrumb('Баннеры киоска', true),
        },
        children: [
          {
            index: true,
            element: <KioskBannersPage />,
            handle: {
              label: 'Баннеры киоска',
              roles: roleSet.adminToSuperAdmin,
            },
          },
          {
            path: `:id`,
            element: (
              <SuspenseContainer>
                <KioskBannerPage />
              </SuspenseContainer>
            ),
            async lazy() {
              const { kioskBannerLoader } = await import(
                'src/pages/promotions/KioskBannerPage/kioskBannerLoader'
              );
              return {
                loader: kioskBannerLoader,
              };
            },
          },
        ],
      },
      {
        path: `splash-screens`,
        handle: {
          crumb: getCrumb('Экраны загрузки', true),
        },
        children: [
          {
            index: true,
            element: <SplashScreensPage />,
            handle: {
              label: 'Экраны загрузки',
              roles: roleSet.adminToSuperAdmin,
              nonFranchiseeAccessOnly: true,
            },
          },
          {
            path: `:id`,
            element: (
              <SuspenseContainer>
                <SplashScreenPage />
              </SuspenseContainer>
            ),
            async lazy() {
              const { splashScreenLoader } = await import(
                'src/pages/promotions/SplashScreenPage/splashScreenLoader'
              );
              return {
                loader: splashScreenLoader,
              };
            },
          },
        ],
      },
    ],
  },
];
